import { useEffect, useRef } from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsButton from '../LdsButton/index.jsx';

import getFocusableElements from '../../util/getFocusableElements.js';

function LdsModal({
  ariaLabelledBy = '',
  children,
  closeBtnAriaLabel = 'close modal',
  disableModalTabFocus = false,
  heading,
  modalId,
  modalSizeClass = 'col-12 col-md-9',
  noPadding = false,
  open = false,
  persistent = false,
  setModalOpen,
  className,
  ...rest
}) {
  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);

  const resetBodyScroll = () => {
    // When closing a modal allow the body to scroll again
    // and reset the scroll position to where it was when the modal was opened.
    const htmlTag = document.documentElement;
    const pagePosition = htmlTag.getAttribute('data-modal-position'); // scroll position is set when modal opened

    htmlTag.setAttribute('data-modal-active', 'false');
    htmlTag.style.top = null;
    window.scrollTo(0, pagePosition);

    htmlTag.removeAttribute('data-modal-position');
  };

  const removeBodyScroll = () => {
    // Prevents the body from scrolling underneath the modal when it opens.
    const htmlTag = document.documentElement;
    const pagePosition = htmlTag.scrollTop || window.pageYOffset;

    htmlTag.style.top = `-${pagePosition}px`;
    htmlTag.setAttribute('data-modal-active', 'true');
    htmlTag.setAttribute('data-modal-position', pagePosition);
  };

  const handleOpen = () => {
    removeBodyScroll();
    try {
      const focusableElements = getFocusableElements(modalRef.current);
      focusableElements[0].focus();
    } catch {
      // no focusable element
    }
  };

  const handleBackdropClick = (e) => {
    if (persistent) return;
    if (e.target.closest('.lds-modal-window')) return; // clicked inside modal window

    setModalOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!persistent && e.code === 'Escape') {
      resetBodyScroll();
      setModalOpen(false);
    }

    const focusableElements = getFocusableElements(modalRef.current);
    const focusableLength = focusableElements.length - 1;
    const isShift = e.shiftKey;

    // Trying to shift tab away from first focusable element
    // Focus on last focusable element
    if (e.target === focusableElements[0] && isShift && e.code === 'Tab' && !disableModalTabFocus) {
      e.preventDefault();
      focusableElements[focusableLength].focus();
    }

    // Trying to tab away from last focusable element
    // Focus on first focusable element
    if (e.target === focusableElements[focusableLength] && !isShift && e.code === 'Tab' && !disableModalTabFocus) {
      e.preventDefault();
      focusableElements[0].focus();
    }
  };

  useEffect(() => {
    if (open) {
      handleOpen();
    } else {
      resetBodyScroll();
    }
  }, [open]);

  const ModalCloseIcon = () => {
    if (persistent) return '';

    return (
      <LdsButton
        clearDefaultClasses
        className="lds-button-clear-style lds-modal-close-btn focus icon"
        onClick={() => setModalOpen(false)}
        ref={closeBtnRef}
        aria-label={closeBtnAriaLabel}
        icon="X"
        iconOnly
      />
    );
  };

  const ModalHeading = () => {
    if (!heading) return '';

    return (
      <h2 id={`${modalId}-heading`}>{heading}</h2>
    );
  };

  return (
    <div
      id={modalId}
      className={cn(
        'lds-modal',
        {
          open,
        },
        className
      )}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      ref={modalRef}
      {...rest}
    >
      <div className="wrapper">
        <div className="row justify-content-center">
          <div className={modalSizeClass}>
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={heading ? `${modalId}-heading` : ariaLabelledBy}
              className={cn(
                'lds-modal-window',
                {
                  'no-padding': noPadding,
                }
              )}
            >
              <ModalCloseIcon />
              <div className="lds-modal-body">
                <ModalHeading />
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

LdsModal.propTypes = {
  ariaLabelledBy: PropTypes.string,
  closeBtnAriaLabel: PropTypes.string,
  disableModalTabFocus: PropTypes.bool,
  modalId: PropTypes.string.isRequired,
  heading: PropTypes.string,
  modalSizeClass: PropTypes.string,
  noPadding: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  persistent: PropTypes.bool,
  setModalOpen: PropTypes.func.isRequired,
};

export default LdsModal;
